import * as Popover from '@radix-ui/react-popover';
import { unreadBadge } from '@smbsuite/api/day';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { notificationsApi } from '../../api/endpoints';
import { useI18n } from '../../i18n';
import { lastCompany, useCompanies } from '../companies/hooks';
import { notificationTarget } from './target';
import { Icon, useUndoToast } from '../../ui';

export function NotificationBell() {
  const { t, tp, date } = useI18n();
  const [open, setOpen] = useState(false);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const companies = useCompanies();
  const unread = useQuery({ queryKey: ['notifications', 'unread'], queryFn: notificationsApi.unread, refetchInterval: 30_000 });
  const list = useQuery({ queryKey: ['notifications', 'list'], queryFn: notificationsApi.list, enabled: open });
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['notifications'] });
  const markRead = useMutation({ mutationFn: notificationsApi.markRead, onSuccess: refresh });
  const offerUndo = useUndoToast();
  const markAll = useMutation({
    mutationFn: notificationsApi.markAllRead,
    // Dismissed all by a slip: back to unread in one tap (TF-94).
    onSuccess: ({ ids }) => {
      if (ids.length > 0) offerUndo(tp('notifications.markedRead', ids.length), () => notificationsApi.markUnread(ids), { after: refresh });
      return refresh();
    },
  });
  const count = unread.data ?? 0;
  // The same number as on the phone's Home tab (TF-273).
  const badge = unreadBadge(count);

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger className="icon-btn" aria-label={count > 0 ? tp('notifications.unread', count) : t('notifications.open')}>
        <Icon name="notifications" />
        {badge && (
          <span className="count-dot" aria-hidden>
            {badge}
          </span>
        )}
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content className="popover" align="end" sideOffset={6} aria-label={t('notifications.title')}>
          <div className="popover-head">
            <h2>{t('notifications.title')}</h2>
            {count > 0 && (
              <button type="button" className="btn btn-sm btn-ghost" onClick={() => markAll.mutate()}>
                {t('notifications.markAll')}
              </button>
            )}
          </div>
          <div className="popover-body">
            {list.data?.length === 0 && <p className="muted p-4">{t('notifications.none')}</p>}
            {list.data?.map((notification) => (
              <button
                key={notification.id}
                type="button"
                className="notification"
                data-unread={!notification.readAt}
                onClick={() => {
                  if (!notification.readAt) markRead.mutate(notification.id);
                  setOpen(false);
                  navigate(notificationTarget(notification, lastCompany(companies.data ?? [])?.id));
                }}
              >
                <strong>{notification.title}</strong>
                {notification.body && <span>{notification.body}</span>}
                <span>
                  {notification.tenantName ? `${notification.tenantName} · ` : ''}
                  {date(notification.createdAt, 'long')}
                </span>
              </button>
            ))}
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
